import { apiService } from './api';

// ============================================================================
// CONTENT MANAGEMENT TYPES
// ============================================================================

export enum ContentStatus {
  Draft = 0,
  Published = 1,
  Archived = 2
}

export type ContentTrashKind =
  | 'Course'
  | 'Level'
  | 'Lesson'
  | 'CourseQuiz'
  | 'LevelQuiz'
  | 'LessonQuiz'
  | 'CourseExercise'
  | 'LevelExercise'
  | 'LessonExercise'
  | 'LibraryBook';

export interface ContentRemoveChildCountDto {
  kind: string;
  label: string;
  count: number;
}

export interface ContentRemoveTraceDto {
  key: string;
  label: string;
  userCount: number;
}

export interface ContentRemoveImpactDto {
  kind: ContentTrashKind;
  id: number;
  title: string;
  canRemove: boolean;
  childrenMessage?: string;
  liveChildren: ContentRemoveChildCountDto[];
  isFree: boolean;
  traces: ContentRemoveTraceDto[];
  outcomeIfConfirmed: 'deleted' | 'trashed';
}

export interface ContentRemoveResultDto {
  kind: ContentTrashKind;
  id: number;
  outcome: 'deleted' | 'trashed';
}

export interface ContentTrashItemDto {
  kind: ContentTrashKind;
  id: number;
  title: string;
  titleFa?: string;
  parentPath?: string;
  trashedAt?: string;
  canRestore: boolean;
  restoreBlockedReason?: string;
}

export enum ContentBlockType {
  Text = 0,
  Audio = 1,
  Exercise = 2,
  Image = 3,
  Video = 4,
  PageSeparator = 5,
  TextAudio = 6,
  Vocabulary = 7
}

export enum WordPartOfSpeech {
  Noun = 0,
  Verb = 1,
  Adjective = 2,
  Adverb = 3,
  Phrase = 4,
  Other = 5
}

/** End-of-lesson Practice modal vs inline content block. */
export enum LessonExercisePlacement {
  EndOfLesson = 0,
  InlineInContent = 1
}

export enum ExerciseType {
  FillInTheBlank = 0,
  MultipleChoice = 1,
  Matching = 2,
  TrueFalse = 3,
  Ordering = 4,
  Puzzle = 5
}

export enum QuestionType {
  MultipleChoice = 0,
  TrueFalse = 1,
  FillInTheBlank = 2,
  ShortAnswer = 3,
  Essay = 4,
  Puzzle = 5,
  Crossword = 7,
  FindTheWords = 8,
  SortParagraphs = 9
}

// Course DTOs
export interface CourseDto {
  id: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  thumbnailUrl?: string;
  backgroundImageUrl?: string;
  language: string;
  difficulty: string;
  status: ContentStatus;
  displayOrder: number;
  /** XP awarded when the learner passes this course. */
  xpScore?: number;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  levels: LevelDto[];
  quizzes: CourseQuizDto[];
  exercises?: CourseExerciseSummaryDto[];
  subscriptionPlanIds: number[];
  /** Set when levels not loaded (e.g. summary); used for row stats. */
  levelCount?: number;
  lessonCount?: number;
  /** Total exercises (course + levels + lessons) when levels not loaded. */
  totalExerciseCount?: number;
  /** Total quizzes (course + levels + lessons) when levels not loaded. */
  totalQuizCount?: number;
  /** Course-level exercise count (prefer over exercises.length for display). */
  courseExerciseCount?: number;
  /** Course-level quiz count (prefer over quizzes.length for display). */
  courseQuizCount?: number;
}

export interface CreateUpdateCourseDto {
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  thumbnailUrl?: string;
  backgroundImageUrl?: string;
  language?: string;
  difficulty?: string;
  status?: ContentStatus;
  displayOrder?: number;
  /** XP awarded when the learner passes this course. */
  xpScore?: number;
  subscriptionPlanIds?: number[];
}

// Level DTOs
export interface LevelDto {
  id: number;
  courseId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  prerequisites?: string;
  recommendedPace?: string;
  isEntryPoint: boolean;
  status: ContentStatus;
  /** XP awarded when the learner passes this level. */
  xpScore?: number;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  lessons: LessonDto[];
  quizzes: LevelQuizDto[];
  exercises?: LevelExerciseSummaryDto[];
  subscriptionPlanIds: number[];
  /** Set when lessons not loaded (e.g. summary); used for row stats. */
  lessonCount?: number;
  /** Total exercises (level + lessons) when lessons not loaded. */
  totalExerciseCount?: number;
  /** Total quizzes (level + lesson quizzes) when lessons not loaded. */
  totalQuizCount?: number;
  /** Level-level exercise count (prefer over exercises.length for display). */
  levelExerciseCount?: number;
  /** Level-level quiz count (prefer over quizzes.length for display). */
  levelQuizCount?: number;
}

export interface CreateUpdateLevelDto {
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder?: number;
  prerequisites?: string;
  recommendedPace?: string;
  isEntryPoint?: boolean;
  status?: ContentStatus;
  /** XP awarded when the learner passes this level. */
  xpScore?: number;
  subscriptionPlanIds?: number[];
}

// Lesson DTOs
export interface LessonDto {
  id: number;
  levelId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  thumbnailUrl?: string;
  backgroundImageUrl?: string;
  displayOrder: number;
  status: ContentStatus;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  contentBlocks: ContentBlockDto[];
  /** Block count when summary endpoint is used (contentBlocks not loaded). */
  contentBlockCount?: number;
  lessonQuiz?: LessonQuizDto;
  exercises?: LessonExerciseSummaryDto[];
  categoryIds?: number[];
  tagIds?: number[];
  visibleInStructuredPath?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  requiresQuizToPass?: boolean;
  /** XP awarded when the learner passes this lesson. */
  xpScore?: number;
}

export interface CreateUpdateLessonDto {
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  thumbnailUrl?: string;
  backgroundImageUrl?: string;
  displayOrder?: number;
  status?: ContentStatus;
  contentBlocks?: CreateUpdateContentBlockDto[];
  lessonQuiz?: CreateUpdateLessonQuizDto;
  subscriptionPlanIds?: number[];
  categoryIds?: number[];
  tagIds?: number[];
  visibleInStructuredPath?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  requiresQuizToPass?: boolean;
  /** XP awarded when the learner passes this lesson. */
  xpScore?: number;
}

// Content Block DTOs
export interface ContentBlockDto {
  id: number;
  lessonId: number;
  type: ContentBlockType;
  displayOrder: number;
  title?: string;
  titleFa?: string;
  textContent?: string;
  textContentFa?: string;
  audioUrl?: string;
  audioFileName?: string;
  originalAudioUrl?: string;
  voiceStylePresetId?: string | null;
  imageUrl?: string;
  videoUrl?: string;
  transcript?: string;
  durationSeconds?: number;
  tips?: string; // JSON array of tips
  stickyAudio?: string; // JSON array of sticky audio entries
  tipsFa?: string; // JSON array of tips (Persian)
  textFormatting?: string; // JSON text formatting configuration
  textFormattingFa?: string;
  isDownloadable?: boolean;
  audioSyncData?: string; // JSON array of audio synchronization mappings
  /** When type is Exercise, the lesson exercise rendered inline at this position. */
  lessonExerciseId?: number;
  createdAt: string;
  updatedAt?: string;
  categoryIds?: number[];
  vocabularyItems?: ContentVocabularyItemDto[];
}

export interface ContentVocabularyItemDto {
  id?: number;
  entryKey: string;
  persian: string;
  english: string;
  phonetic?: string;
  partOfSpeech?: WordPartOfSpeech;
  example?: string;
  imageUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  originalAudioUrl?: string;
  voiceStylePresetId?: string | null;
  displayOrder?: number;
}

export interface CreateUpdateContentBlockDto {
  id?: number;
  /** Client-only key so a new block keeps its identity while it is reordered. The API ignores it. */
  reorderKey?: string;
  type: ContentBlockType;
  displayOrder?: number;
  title?: string;
  titleFa?: string;
  textContent?: string;
  textContentFa?: string;
  audioUrl?: string;
  audioFileName?: string;
  originalAudioUrl?: string;
  voiceStylePresetId?: string | null;
  imageUrl?: string;
  videoUrl?: string;
  transcript?: string;
  durationSeconds?: number;
  tips?: string; // JSON array of tips
  stickyAudio?: string; // JSON array of sticky audio entries
  tipsFa?: string; // JSON array of tips (Persian)
  textFormatting?: string; // JSON text formatting configuration
  textFormattingFa?: string;
  isDownloadable?: boolean;
  audioSyncData?: string; // JSON array of audio synchronization mappings
  /** When type is Exercise, the lesson exercise rendered inline at this position. */
  lessonExerciseId?: number;
  categoryIds?: number[];
  vocabularyItems?: ContentVocabularyItemDto[];
}

export interface StickyAudioMapEntryDto {
  phrase: string;
  phraseKey: string;
  audioUrl: string;
  audioFileName?: string;
}

// Lesson Quiz DTOs
export interface LessonQuizDto {
  id: number;
  lessonId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  includeInKnowledgePlacement?: boolean;
  allowUnansweredQuestionsOnSubmit?: boolean;
  shuffleQuestionsOnEachLoad?: boolean;
  mandatoryForPassingLesson?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  displayOrder?: number;
  timeLimitMinutes: number;
  passingScore: number;
  xpScore?: number;
  status?: ContentStatus;
  userBestQuizScore?: number;
  userHasPassedQuiz?: boolean;
  createdAt: string;
  updatedAt?: string;
  questions: LessonQuizQuestionDto[];
  categoryIds?: number[];
}

export interface CreateUpdateLessonQuizDto {
  id?: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  includeInKnowledgePlacement?: boolean;
  allowUnansweredQuestionsOnSubmit?: boolean;
  shuffleQuestionsOnEachLoad?: boolean;
  mandatoryForPassingLesson?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  displayOrder?: number;
  timeLimitMinutes?: number;
  passingScore?: number;
  xpScore?: number;
  questions: CreateUpdateLessonQuizQuestionDto[];
  categoryIds?: number[];
}

export interface LessonQuizQuestionOptionDto {
  id: number;
  lessonQuizQuestionId: number;
  displayOrder: number;
  correctOrder?: number; // For puzzle questions: correct order for validation
  startRow?: number;
  startCol?: number;
  wordDirection?: string;
  entryWord?: string;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  isCorrect: boolean;
}

export interface LessonQuizQuestionDto {
  id: number;
  lessonQuizId: number;
  question: string; // Legacy, kept for backward compatibility
  correctAnswer: string; // Legacy, kept for backward compatibility
  options?: string; // Legacy, kept for backward compatibility
  textContent?: string; // Block-based question content
  textFormatting?: string; // JSON formatting config
  audioFileName?: string;
  audioUrl?: string;
  imageUrl?: string;
  videoUrl?: string;
  answerImageUrl?: string;
  answerAudioUrl?: string;
  answerVideoUrl?: string;
  explanation?: string;
  explanationFa?: string;
  points: number;
  displayOrder: number;
  questionType: QuestionType;
  puzzleAlignment?: 'ltr' | 'rtl'; // For puzzle questions: alignment of cards
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  correctOptionId?: number; // Reference to correct option
  questionOptions?: LessonQuizQuestionOptionDto[]; // Structured options
  shuffleMultipleChoiceOptions?: boolean;
}

export interface CreateUpdateLessonQuizQuestionDto {
  id?: number;
  question: string;
  correctAnswer: string;
  options?: string;
  audioFileName?: string;
  audioUrl?: string;
  imageUrl?: string;
  videoUrl?: string;
  answerImageUrl?: string;
  answerAudioUrl?: string;
  answerVideoUrl?: string;
  explanation?: string;
  explanationFa?: string;
  points?: number;
  displayOrder?: number;
  questionType?: QuestionType;
  puzzleAlignment?: 'ltr' | 'rtl'; // For puzzle questions: alignment of cards
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  correctOptionId?: number;
  questionOptions?: LessonQuizQuestionOptionDto[];
  shuffleMultipleChoiceOptions?: boolean;
}

// Course Quiz DTOs
export interface CourseQuizDto {
  id: number;
  courseId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  includeInKnowledgePlacement?: boolean;
  allowUnansweredQuestionsOnSubmit?: boolean;
  shuffleQuestionsOnEachLoad?: boolean;
  displayOrder: number;
  timeLimitMinutes: number;
  passingScore: number;
  xpScore?: number;
  mandatoryForPassingCourse?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  status: ContentStatus;
  createdAt: string;
  updatedAt?: string;
  questions: CourseQuizQuestionDto[];
  previousScore?: number;
  questionCount?: number; // Question count from summary views
}

export interface CreateUpdateCourseQuizDto {
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  includeInKnowledgePlacement?: boolean;
  allowUnansweredQuestionsOnSubmit?: boolean;
  shuffleQuestionsOnEachLoad?: boolean;
  displayOrder?: number;
  timeLimitMinutes?: number;
  passingScore?: number;
  xpScore?: number;
  mandatoryForPassingCourse?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  questions: CreateUpdateCourseQuizQuestionDto[];
}

export interface CourseQuizQuestionOptionDto {
  id: number;
  courseQuizQuestionId: number;
  displayOrder: number;
  correctOrder?: number; // For puzzle questions: correct order for validation
  startRow?: number;
  startCol?: number;
  wordDirection?: string;
  entryWord?: string;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  isCorrect: boolean;
}

export interface CourseQuizQuestionDto {
  id: number;
  courseQuizId: number;
  question: string; // Legacy, kept for backward compatibility
  correctAnswer: string; // Legacy, kept for backward compatibility
  options?: string; // Legacy, kept for backward compatibility
  textContent?: string; // Block-based question content
  textFormatting?: string; // JSON formatting config
  explanation?: string;
  explanationFa?: string;
  points: number;
  displayOrder: number;
  questionType: ExerciseType;
  puzzleAlignment?: 'ltr' | 'rtl'; // For puzzle questions: alignment of cards
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  answerImageUrl?: string;
  answerAudioUrl?: string;
  answerVideoUrl?: string;
  correctOptionId?: number; // Reference to correct option
  questionOptions?: CourseQuizQuestionOptionDto[]; // Structured options
  shuffleMultipleChoiceOptions?: boolean;
}

export interface CreateUpdateCourseQuizQuestionDto {
  id?: number;
  question: string;
  correctAnswer: string;
  options?: string;
  explanation?: string;
  explanationFa?: string;
  points?: number;
  displayOrder?: number;
  questionType?: ExerciseType;
  puzzleAlignment?: 'ltr' | 'rtl';
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  answerImageUrl?: string;
  answerAudioUrl?: string;
  answerVideoUrl?: string;
  correctOptionId?: number;
  questionOptions?: CourseQuizQuestionOptionDto[];
  shuffleMultipleChoiceOptions?: boolean;
}

// Level Quiz DTOs
export interface LevelQuizDto {
  id: number;
  levelId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  includeInKnowledgePlacement?: boolean;
  allowUnansweredQuestionsOnSubmit?: boolean;
  shuffleQuestionsOnEachLoad?: boolean;
  displayOrder: number;
  timeLimitMinutes: number;
  passingScore: number;
  xpScore?: number;
  mandatoryForPassingLevel?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  status: ContentStatus;
  createdAt: string;
  updatedAt?: string;
  questions: LevelQuizQuestionDto[];
  previousScore?: number;
  questionCount?: number; // Question count from summary views
}

export interface CreateUpdateLevelQuizDto {
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  includeInKnowledgePlacement?: boolean;
  allowUnansweredQuestionsOnSubmit?: boolean;
  shuffleQuestionsOnEachLoad?: boolean;
  displayOrder?: number;
  timeLimitMinutes?: number;
  passingScore?: number;
  xpScore?: number;
  mandatoryForPassingLevel?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  questions: CreateUpdateLevelQuizQuestionDto[];
}

export interface LevelQuizQuestionOptionDto {
  id: number;
  levelQuizQuestionId: number;
  displayOrder: number;
  correctOrder?: number; // For puzzle questions: correct order for validation
  startRow?: number;
  startCol?: number;
  wordDirection?: string;
  entryWord?: string;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  isCorrect: boolean;
}

export interface LevelQuizQuestionDto {
  id: number;
  levelQuizId: number;
  question: string; // Legacy, kept for backward compatibility
  correctAnswer: string; // Legacy, kept for backward compatibility
  options?: string; // Legacy, kept for backward compatibility
  textContent?: string; // Block-based question content
  textFormatting?: string; // JSON formatting config
  explanation?: string;
  explanationFa?: string;
  points: number;
  displayOrder: number;
  questionType: ExerciseType;
  puzzleAlignment?: 'ltr' | 'rtl'; // For puzzle questions: alignment of cards
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  answerImageUrl?: string;
  answerAudioUrl?: string;
  answerVideoUrl?: string;
  correctOptionId?: number; // Reference to correct option
  questionOptions?: LevelQuizQuestionOptionDto[]; // Structured options
  shuffleMultipleChoiceOptions?: boolean;
}

export interface CreateUpdateLevelQuizQuestionDto {
  id?: number;
  question: string;
  correctAnswer: string;
  options?: string;
  explanation?: string;
  explanationFa?: string;
  points?: number;
  displayOrder?: number;
  questionType?: ExerciseType;
  puzzleAlignment?: 'ltr' | 'rtl';
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  answerImageUrl?: string;
  answerAudioUrl?: string;
  answerVideoUrl?: string;
  correctOptionId?: number;
  questionOptions?: LevelQuizQuestionOptionDto[];
  shuffleMultipleChoiceOptions?: boolean;
}

// Reorder DTOs
export interface ReorderItemsDto {
  items: ItemOrderDto[];
}

export interface ItemOrderDto {
  id: number;
  displayOrder: number;
}

// Subscriber DTOs
export interface CourseCatalogDto {
  id: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  thumbnailUrl?: string;
  backgroundImageUrl?: string;
  difficulty: string;
  levelCount: number;
  lessonCount: number;
  /** Published course-level quizzes (matches course detail header). */
  courseQuizCount?: number;
  /** Published course-level exercises. */
  courseExerciseCount?: number;
  /** Published quizzes on the course, its levels, and its lessons. */
  totalQuizCount?: number;
  /** Published exercises on the course, its levels, and its lessons. */
  totalExerciseCount?: number;
  userProgressPercentage?: number;
  isAccessible: boolean;
  accessMessage?: string;
  /** True when the learner has fully passed this course. */
  isCompleted?: boolean;
  /** Course or level has mandatory published quizzes/exercises. */
  hasMandatoryAssessments?: boolean;
  /** Lessons complete but mandatory assessments remain. */
  mandatoryAssessmentsPending?: boolean;
}

export interface PublicQuickLaunchCardDto {
  cardKey: string;
  color: string;
  backgroundImageUrl?: string | null;
  displayOrder: number;
}

export interface LearnerSearchRequest {
  query: string;
  searchCourseTitles?: boolean;
  searchLevelTitles?: boolean;
  searchLessonTitles?: boolean;
  searchBlockTitles?: boolean;
  searchTextBlocks?: boolean;
  searchCourseCatalog?: boolean;
  searchLevelCatalog?: boolean;
  searchLessonQuizzes?: boolean;
  searchLessonExercises?: boolean;
  searchLibraryBookTitles?: boolean;
  searchLibraryBookText?: boolean;
  searchVocabulary?: boolean;
  searchLeitner?: boolean;
  limitToLearningContext?: boolean;
  scopeCourseId?: number;
  scopeLevelId?: number;
  scopeLessonId?: number;
  scopeLimitToLibrary?: boolean;
  scopeLibraryBookId?: number;
}

/** Title | Text | Quiz | Exercise | CourseCatalog | LevelCatalog | LibraryTitle | LibraryText | Vocabulary | Leitner */
export type LearnerSearchMatchKind =
  | 'Title'
  | 'Text'
  | 'Quiz'
  | 'Exercise'
  | 'CourseCatalog'
  | 'LevelCatalog'
  | 'LibraryTitle'
  | 'LibraryText'
  | 'Vocabulary'
  | 'Leitner';

/** Subscription plan returned alongside locked search hits so the UI can show the unlock modal. */
export interface LearnerSearchRequiredPlan {
  id: number;
  name: string;
  description?: string;
}

export interface LearnerSearchResult {
  matchKind: LearnerSearchMatchKind;
  subKind: string;
  label: string;
  snippet?: string;
  rankOrder?: number;
  courseId: number;
  courseTitle: string;
  levelId?: number;
  levelTitle?: string;
  lessonId?: number;
  lessonTitle?: string;
  contentBlockId?: number;
  blockTitle?: string;
  lessonQuizId?: number;
  lessonExerciseId?: number;
  libraryBookId?: number;
  libraryBookTitle?: string;
  libraryBookBlockId?: number;
  leitnerCardId?: number;
  /** True when the result lives behind a subscription the user does not own. */
  isLocked?: boolean;
  /** Course | Level | Lesson | Exercise | Book – nearest ancestor that gates access. */
  lockedAt?: 'Course' | 'Level' | 'Lesson' | 'Exercise' | 'Book';
  /** Plans that would unlock this hit. Empty when not locked. */
  requiredPlans?: LearnerSearchRequiredPlan[];
}

export interface CourseDetailDto {
  id: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  thumbnailUrl?: string;
  backgroundImageUrl?: string;
  difficulty: string;
  quizzes: CourseQuizSummaryDto[];
  exercises: CourseExerciseSummaryDto[];
  items: CourseItemDto[];
  levels: LevelDetailDto[];
  userProgressPercentage?: number;
  currentLevelId?: number;
  currentLessonId?: number;
  isAccessible: boolean;
  accessMessage?: string;
  hasPlacementQuiz: boolean;
  placementQuizId?: number;
  subscriptionPlanIds?: number[];
  isCompleted?: boolean;
  hasMandatoryAssessments?: boolean;
  mandatoryAssessmentsPending?: boolean;
}

export interface CourseExerciseSummaryDto {
  id: number;
  courseId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  questionCount: number;
  status?: ContentStatus;
  subscriptionPlanIds?: number[];
  /** True when the current user has marked this course exercise as completed/reviewed. */
  userHasCompletedExercise?: boolean;
  mandatoryForPassingCourse?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  /** Set when rendering a level exercise in the course detail panel. */
  mandatoryForPassingLevel?: boolean;
}

export interface LevelExerciseSummaryDto {
  id: number;
  levelId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  questionCount: number;
  status?: ContentStatus;
  subscriptionPlanIds?: number[];
  /** True when the current user has marked this level exercise as completed/reviewed. */
  userHasCompletedExercise?: boolean;
  mandatoryForPassingLevel?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
}

export interface LessonExerciseSummaryDto {
  id: number;
  lessonId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  questionCount: number;
  status?: ContentStatus;
  subscriptionPlanIds?: number[];
  categoryIds?: number[];
  mandatoryForPassingLesson?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  userHasCompletedExercise?: boolean;
  /** Placed inline in lesson content (not in end-of-lesson Practice). */
  isInlineInLessonBody?: boolean;
  placement?: LessonExercisePlacement;
}

export interface LevelDetailDto {
  id: number;
  courseId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  recommendedPace?: string;
  quizzes: LevelQuizSummaryDto[];
  exercises: LevelExerciseSummaryDto[];
  items: LevelItemDto[];
  lessons: LessonSummaryDto[];
  isAccessible: boolean;
  accessMessage?: string;
  subscriptionPlanIds?: number[];
  isCompleted?: boolean;
  hasMandatoryAssessments?: boolean;
  mandatoryAssessmentsPending?: boolean;
}

// Subscriber course quiz summary
export interface CourseQuizSummaryDto {
  id: number;
  courseId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  timeLimitMinutes: number;
  passingScore: number;
  questionCount: number;
  /** Current user's best (highest) percentage score for this course quiz, if any. */
  userBestQuizScore?: number;
  /** True when the current user has passed this course quiz at least once. */
  userHasPassedQuiz?: boolean;
  mandatoryForPassingCourse?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
}

// Mixed course items (levels + course quizzes + course exercises) ordered by displayOrder
export interface CourseItemDto {
  type: 'level' | 'quiz' | 'exercise';
  id: number;
  title: string;
  displayOrder: number;
  data?: LevelDetailDto | CourseQuizSummaryDto | CourseExerciseSummaryDto | null;
}

// Subscriber level quiz summary
export interface LevelQuizSummaryDto {
  id: number;
  levelId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  timeLimitMinutes: number;
  passingScore: number;
  questionCount: number;
  /** Current user's best (highest) percentage score for this level quiz, if any. */
  userBestQuizScore?: number;
  /** True when the current user has passed this level quiz at least once. */
  userHasPassedQuiz?: boolean;
  mandatoryForPassingLevel?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
}

// Mixed level items (lessons + level quizzes + level exercises) ordered by displayOrder
export interface LevelItemDto {
  type: 'lesson' | 'quiz' | 'exercise';
  id: number;
  title: string;
  displayOrder: number;
  data?: LessonSummaryDto | LevelQuizSummaryDto | LevelExerciseSummaryDto | null;
}

export interface LessonSummaryDto {
  id: number;
  levelId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  /** 1-based place among lessons included in the structured course path. */
  lessonNumber?: number;
  isCompleted: boolean;
  quizScore?: number;
  /** True when the learner has passed the lesson quiz. */
  isQuizPassed?: boolean;
  /** True when the lesson has an associated lesson quiz. */
  hasLessonQuiz?: boolean;
  /** Published quizzes on this lesson. */
  publishedQuizCount?: number;
  /** Published exercises on this lesson, including inline exercises. */
  publishedExerciseCount?: number;
  isAccessible: boolean;
  contentBlockCount: number;
  /** Number of content blocks the user has viewed. */
  viewedBlocksCount?: number;
  contentBlockCountsByType: {
    text: number;
    audio: number;
    exercise: number;
    image: number;
    video: number;
  };
  subscriptionPlanIds?: number[];
  /** True when the lesson is in the current user's favorites. */
  isFavorite?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
}

export interface RequiredSubscriptionPlanDto {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
}

export interface LessonDetailDto {
  id: number;
  levelId: number;
  courseId: number;
  courseTitle?: string;
  courseTitleFa?: string;
  levelTitle?: string;
  levelTitleFa?: string;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  thumbnailUrl?: string;
  backgroundImageUrl?: string;
  displayOrder: number;
  /** 1-based place among lessons included in the structured course path. */
  lessonNumber?: number;
  contentBlocks: ContentBlockDto[];
  lessonQuizzes?: LessonQuizDto[];
  lessonQuiz?: LessonQuizDto;
  exercises?: LessonExerciseSummaryDto[];
  isCompleted: boolean;
  isLessonReviewed?: boolean;
  previousQuizScore?: number;
  isAccessible: boolean;
  accessMessage?: string;
  nextLessonId?: number;
  previousLessonId?: number;
  nextLessonIsDesignedForNonFarsiUsers?: boolean;
  previousLessonIsDesignedForNonFarsiUsers?: boolean;
  /** @deprecated Use mandatoryForPassingLesson on each quiz/exercise */
  requiresQuizToPass?: boolean;
  visibleInStructuredPath?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  isFavorite: boolean;
  viewCount?: number;
  requiredSubscriptionPlans?: RequiredSubscriptionPlanDto[];
}

export interface CompleteLessonExerciseRequestDto {
  answers: { questionId: number; answer?: string }[];
}

export interface CompleteLessonExerciseResultDto {
  passed: boolean;
  questionResults: ExerciseQuestionCheckResultDto[];
}

export interface UpdateLessonProgressDto {
  lessonId: number;
  isCompleted?: boolean;
  isFinished?: boolean;
  markPassedWithoutQuiz?: boolean;
  unmarkPassed?: boolean;
}

export interface LessonQuizSubmissionDto {
  lessonQuizId: number;
  answers: LessonQuizAnswerDto[];
  /** Wall-clock seconds from Start to submit (optional). */
  totalTimeSpentSeconds?: number;
  /** True when auto-submitting because the countdown reached zero. */
  submittedOnTimeExpiry?: boolean;
}

export interface LessonQuizAnswerDto {
  questionId: number;
  answer: string;
}

export interface LessonQuizResultDto {
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  passed: boolean;
  passingScore: number;
  questionResults: QuestionResultDto[];
  xpAwarded?: number;
  XpAwarded?: number;
}

// Quiz Flow DTOs
export interface LevelQuizSubmissionDto {
  levelQuizId: number;
  answers: LevelQuizAnswerDto[];
  totalTimeSpentSeconds?: number;
  submittedOnTimeExpiry?: boolean;
}

export interface LevelQuizAnswerDto {
  questionId: number;
  answer: string;
}

export interface LevelQuizResultDto {
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  passed: boolean;
  passingScore: number;
  questionResults: QuestionResultDto[];
  recommendationMessage?: string;
  recommendedCourseId?: number;
  recommendedLevelId?: number;
  recommendedCourseName?: string;
  recommendedLevelName?: string;
}

export interface LevelQuizWithContextDto {
  id: number;
  levelId: number;
  courseId: number;
  courseName: string;
  levelName: string;
  title: string;
  description?: string;
  displayOrder: number;
  timeLimitMinutes: number;
  passingScore: number;
  questions: LevelQuizQuestionDto[];
  isCompleted: boolean;
  previousScore?: number;
}

export interface QuizFlowItemDto {
  kind: string;
  quizId: number;
  courseId: number;
  levelId?: number | null;
  lessonId?: number | null;
  /** Lesson to open for "View Full Lesson" (level/course quizzes). */
  viewLessonId?: number | null;
  title: string;
  titleFa?: string;
  contextSubtitle: string;
  contextSubtitleFa?: string;
  isPassed: boolean;
  bestScorePercent?: number | null;
  passingScore?: number | null;
  includeInKnowledgePlacement?: boolean | null;
  isDesignedForNonFarsiUsers?: boolean;
}

export interface ExerciseFlowItemDto {
  kind: string;
  exerciseId: number;
  courseId: number;
  levelId?: number | null;
  lessonId?: number | null;
  viewLessonId?: number | null;
  title: string;
  titleFa?: string;
  contextSubtitle: string;
  contextSubtitleFa?: string;
  isReviewedByUser: boolean;
  /** Plan IDs attached to this exercise (empty = free / inherits parent). */
  subscriptionPlanIds: number[];
  /** Server-computed: user may open this exercise. */
  isAccessibleToUser: boolean;
  isDesignedForNonFarsiUsers?: boolean;
}

/** Normalize API booleans (camelCase / PascalCase / string). */
function coerceApiBoolean(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0' || s === '') return false;
  }
  return Boolean(v);
}

function optionalId(a: unknown, b: unknown): number | null {
  const v = a ?? b;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function coerceIntArray(v: unknown): number[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => Number(x)).filter((n) => Number.isFinite(n));
}

function normalizeQuizFlowItem(row: unknown): QuizFlowItemDto {
  const r = row as Record<string, unknown>;
  return {
    kind: String(r.kind ?? r.Kind ?? 'lesson').toLowerCase(),
    quizId: Number(r.quizId ?? r.QuizId ?? 0),
    courseId: Number(r.courseId ?? r.CourseId ?? 0),
    levelId: optionalId(r.levelId, r.LevelId),
    lessonId: optionalId(r.lessonId, r.LessonId),
    viewLessonId: optionalId(r.viewLessonId, r.ViewLessonId),
    title: String(r.title ?? r.Title ?? ''),
    titleFa: (r.titleFa ?? r.TitleFa) != null ? String(r.titleFa ?? r.TitleFa) : undefined,
    contextSubtitle: String(r.contextSubtitle ?? r.ContextSubtitle ?? ''),
    contextSubtitleFa:
      (r.contextSubtitleFa ?? r.ContextSubtitleFa) != null
        ? String(r.contextSubtitleFa ?? r.ContextSubtitleFa)
        : undefined,
    isPassed: coerceApiBoolean(r.isPassed ?? r.IsPassed),
    includeInKnowledgePlacement: (() => {
      const v = r.includeInKnowledgePlacement ?? r.IncludeInKnowledgePlacement;
      if (v == null) return null;
      return coerceApiBoolean(v);
    })(),
    passingScore: (() => {
      const v = r.passingScore ?? r.PassingScore;
      if (v == null || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    })(),
    bestScorePercent: (() => {
      const v = r.bestScorePercent ?? r.BestScorePercent;
      if (v == null || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    })(),
    isDesignedForNonFarsiUsers: coerceApiBoolean(
      r.isDesignedForNonFarsiUsers ?? r.IsDesignedForNonFarsiUsers ?? false
    ),
  };
}

function normalizeExerciseFlowItem(row: unknown): ExerciseFlowItemDto {
  const r = row as Record<string, unknown>;
  return {
    kind: String(r.kind ?? r.Kind ?? 'lesson').toLowerCase(),
    exerciseId: Number(r.exerciseId ?? r.ExerciseId ?? 0),
    courseId: Number(r.courseId ?? r.CourseId ?? 0),
    levelId: optionalId(r.levelId, r.LevelId),
    lessonId: optionalId(r.lessonId, r.LessonId),
    viewLessonId: optionalId(r.viewLessonId, r.ViewLessonId),
    title: String(r.title ?? r.Title ?? ''),
    titleFa: (r.titleFa ?? r.TitleFa) != null ? String(r.titleFa ?? r.TitleFa) : undefined,
    contextSubtitle: String(r.contextSubtitle ?? r.ContextSubtitle ?? ''),
    contextSubtitleFa:
      (r.contextSubtitleFa ?? r.ContextSubtitleFa) != null
        ? String(r.contextSubtitleFa ?? r.ContextSubtitleFa)
        : undefined,
    isReviewedByUser: coerceApiBoolean(r.isReviewedByUser ?? r.IsReviewedByUser),
    subscriptionPlanIds: coerceIntArray(r.subscriptionPlanIds ?? r.SubscriptionPlanIds),
    isAccessibleToUser: coerceApiBoolean(r.isAccessibleToUser ?? r.IsAccessibleToUser ?? true),
    isDesignedForNonFarsiUsers: coerceApiBoolean(
      r.isDesignedForNonFarsiUsers ?? r.IsDesignedForNonFarsiUsers ?? false
    ),
  };
}

export interface QuestionResultDto {
  questionId: number;
  isCorrect: boolean;
  userAnswer: string;
  correctAnswer: string;
}

export interface UserCourseProgressDto {
  courseId: number;
  progressPercentage: number;
  currentLevelId?: number;
  currentLessonId?: number;
  startedAt?: string;
  lastAccessedAt?: string;
  completedAt?: string;
}

// Time Tracking DTOs
export interface BlockTimeTrackingDto {
  contentBlockId: number;
  timeSpent: string; // TimeSpan as string (e.g., "PT2H30M15S")
  lastViewedAt: string;
  viewCount: number;
}

export interface SubmitTimeTrackingDto {
  contentBlockId: number;
  secondsSpent: number;
  /** When false (e.g. tag browse), seconds count toward tags only, not the Lessons chart. Default true. */
  countsTowardLessons?: boolean;
}

export interface RecordExerciseTimeSessionDto {
  scope: 'course' | 'level' | 'lesson';
  exerciseId: number;
  secondsSpent: number;
}

// Enhanced Progress DTOs
export interface CourseProgressWithTimeDto {
  courseId: number;
  progressPercentage: number;
  isCompleted: boolean;
  totalLessons: number;
  completedLessons: number;
  totalLevels: number;
  completedLevels: number;
  courseQuizPassed: boolean;
  totalTimeSpent: string; // TimeSpan as string
  currentLevelId?: number;
  currentLessonId?: number;
  startedAt?: string;
  lastAccessedAt?: string;
  completedAt?: string;
}

export interface LevelProgressWithTimeDto {
  levelId: number;
  progressPercentage: number;
  isCompleted: boolean;
  totalLessons: number;
  completedLessons: number;
  levelQuizPassed: boolean;
  totalTimeSpent: string; // TimeSpan as string
}

// Enhanced Quiz Result DTOs
export interface QuizNavigationGuidanceDto {
  message: string;
  nextActionType: string; // "proceed", "retake", "restart"
  targetId?: number;
  targetType?: string; // "lesson", "level", "course"
  targetTitle?: string;
  canProceed: boolean;
}

export interface EnhancedQuizResultDto {
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  passed: boolean;
  passingScore: number;
  questionResults: QuestionResultDto[];
  failedDueToTimeExpiryIncomplete?: boolean;
  FailedDueToTimeExpiryIncomplete?: boolean;
  navigationGuidance: QuizNavigationGuidanceDto;
  xpAwarded?: number;
  XpAwarded?: number;
}

// Tag Browsing DTOs
export interface TaggedContentBlockDto {
  id: number;
  lessonId: number;
  lessonTitle: string;
  lessonTitleFa?: string;
  levelId: number;
  levelTitle: string;
  levelTitleFa?: string;
  courseId: number;
  courseTitle: string;
  type: ContentBlockType;
  displayOrder: number;
  title?: string;
  titleFa?: string;
  textContent?: string;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  transcript?: string;
  durationSeconds?: number;
  tips?: string;
  stickyAudio?: string;
  textFormatting?: string;
  isDownloadable?: boolean;
  audioSyncData?: string;
  isPassedForTag: boolean;
  /** When the block is an exercise, the lesson exercise it shows. */
  lessonExerciseId?: number;
  /** Shared by blocks that form one learner lesson in this tag. */
  lessonGroupKey?: number | null;
  /** Views of this block as a tag lesson. A merged lesson uses the first block. */
  viewCount?: number;
  /** 1-based place of this block's tag lesson in the admin tag order. */
  lessonNumber?: number;
  vocabularyItems?: ContentVocabularyItemDto[];
}

export interface TaggedContentLessonDto {
  id: number;
  levelId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  /** 1-based place in the admin tag order, including lessons the learner list hides. */
  lessonNumber?: number;
  courseId: number;
  courseTitle: string;
  levelTitle: string;
  isPassedForTag: boolean;
  hasLessonQuiz?: boolean;
  requiresQuizToPass?: boolean;
}

export interface TagProgressDto {
  tagId: number;
  totalBlocks: number;
  passedBlocks: number;
  totalLessons: number;
  passedLessons: number;
  progressPercentage: number;
}

// Placement Quiz Session DTOs
export interface PlacementQuizSessionDto {
  id: number;
  sessionId: string;
  currentLevelQuizId?: number;
  completedLevelIds: number[];
  failedLevelId?: number;
  recommendedStartLevelId?: number;
  status: string; // "in-progress", "completed", "failed"
  startedAt: string;
  lastActivityAt: string;
  completedAt?: string;
  previousRecommendedLevelId?: number;
  previousRecommendedLevelName?: string;
  previousRecommendedCourseName?: string;
}

export interface PlacementQuizLevelDto {
  levelId: number;
  levelTitle: string;
  courseId: number;
  courseTitle: string;
  quizId: number;
  quizTitle: string;
  displayOrder: number;
}

export interface PlacementQuizRecommendationDto {
  recommendedStartLevelId: number;
  recommendedLevelName: string;
  recommendedCourseId: number;
  recommendedCourseName: string;
  message: string;
  explanation: string;
}

export interface PlacementQuizResultDto {
  passed: boolean;
  score: number;
  passingScore: number;
  nextLevel?: PlacementQuizLevelDto;
  recommendation?: PlacementQuizRecommendationDto;
  currentPosition: number;
  totalLevels: number;
}

export interface CourseQuizSubmissionDto {
  courseQuizId: number;
  answers: CourseQuizAnswerDto[];
  totalTimeSpentSeconds?: number;
  submittedOnTimeExpiry?: boolean;
}

export interface CourseQuizAnswerDto {
  questionId: number;
  answer: string;
}

// Exercise DTOs (learner view: full exercise with questions; no time limit / passing score)
export interface CourseExerciseDto {
  id: number;
  courseId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  xpScore: number;
  mandatoryForPassingCourse?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  status: ContentStatus;
  createdAt: string;
  updatedAt?: string;
  questions: CourseExerciseQuestionDto[];
  subscriptionPlanIds?: number[];
  questionCount?: number;
}

export interface CourseExerciseQuestionDto {
  id: number;
  courseExerciseId: number;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  explanation?: string;
  explanationFa?: string;
  points: number;
  displayOrder: number;
  questionType: QuestionType;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  correctOptionId?: number;
  puzzleAlignment?: string;
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  questionOptions: CourseExerciseQuestionOptionDto[];
}

export interface CourseExerciseQuestionOptionDto {
  id: number;
  courseExerciseQuestionId: number;
  displayOrder: number;
  correctOrder?: number;
  startRow?: number;
  startCol?: number;
  wordDirection?: string;
  entryWord?: string;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  isCorrect: boolean;
}

export interface LevelExerciseDto {
  id: number;
  levelId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  xpScore: number;
  mandatoryForPassingLevel?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  status: ContentStatus;
  createdAt: string;
  updatedAt?: string;
  questions: LevelExerciseQuestionDto[];
  subscriptionPlanIds?: number[];
  questionCount?: number;
}

export interface LevelExerciseQuestionDto {
  id: number;
  levelExerciseId: number;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  explanation?: string;
  explanationFa?: string;
  points: number;
  displayOrder: number;
  questionType: QuestionType;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  correctOptionId?: number;
  puzzleAlignment?: string;
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  questionOptions: LevelExerciseQuestionOptionDto[];
}

export interface LevelExerciseQuestionOptionDto {
  id: number;
  levelExerciseQuestionId: number;
  displayOrder: number;
  correctOrder?: number;
  startRow?: number;
  startCol?: number;
  wordDirection?: string;
  entryWord?: string;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  isCorrect: boolean;
}

export interface LessonExerciseDto {
  id: number;
  lessonId: number;
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder: number;
  xpScore: number;
  status: ContentStatus;
  createdAt: string;
  updatedAt?: string;
  questions: LessonExerciseQuestionDto[];
  subscriptionPlanIds?: number[];
  categoryIds?: number[];
  questionCount?: number;
  mandatoryForPassingLesson?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  placement?: LessonExercisePlacement;
}

export interface LessonExerciseQuestionDto {
  id: number;
  lessonExerciseId: number;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  explanation?: string;
  explanationFa?: string;
  points: number;
  displayOrder: number;
  questionType: QuestionType;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  correctOptionId?: number;
  puzzleAlignment?: string;
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  questionOptions: LessonExerciseQuestionOptionDto[];
}

export interface LessonExerciseQuestionOptionDto {
  id: number;
  lessonExerciseQuestionId: number;
  displayOrder: number;
  correctOrder?: number;
  startRow?: number;
  startCol?: number;
  wordDirection?: string;
  entryWord?: string;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  isCorrect: boolean;
}

export interface ExerciseQuestionCheckResultDto {
  correct: boolean;
  explanation?: string;
  explanationFa?: string;
}

// Create/Update Exercise DTOs (admin)
export interface CreateUpdateCourseExerciseDto {
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder?: number;
  xpScore?: number;
  mandatoryForPassingCourse?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  questions: CreateUpdateCourseExerciseQuestionDto[];
  subscriptionPlanIds?: number[];
}

export interface CreateUpdateCourseExerciseQuestionDto {
  id?: number;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  explanation?: string;
  explanationFa?: string;
  points?: number;
  displayOrder?: number;
  questionType?: QuestionType;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  correctOptionId?: number;
  puzzleAlignment?: string;
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  questionOptions?: CreateUpdateCourseExerciseQuestionOptionDto[];
}

export interface CreateUpdateCourseExerciseQuestionOptionDto {
  id?: number;
  displayOrder?: number;
  correctOrder?: number;
  startRow?: number;
  startCol?: number;
  wordDirection?: string;
  entryWord?: string;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  isCorrect?: boolean;
}

export interface CreateUpdateLevelExerciseDto {
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder?: number;
  xpScore?: number;
  mandatoryForPassingLevel?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  questions: CreateUpdateLevelExerciseQuestionDto[];
  subscriptionPlanIds?: number[];
}

export interface CreateUpdateLevelExerciseQuestionDto {
  id?: number;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  explanation?: string;
  explanationFa?: string;
  points?: number;
  displayOrder?: number;
  questionType?: QuestionType;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  correctOptionId?: number;
  puzzleAlignment?: string;
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  questionOptions?: CreateUpdateLevelExerciseQuestionOptionDto[];
}

export interface CreateUpdateLevelExerciseQuestionOptionDto {
  id?: number;
  displayOrder?: number;
  correctOrder?: number;
  startRow?: number;
  startCol?: number;
  wordDirection?: string;
  entryWord?: string;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  isCorrect?: boolean;
}

export interface CreateUpdateLessonExerciseDto {
  title: string;
  titleFa?: string;
  description?: string;
  descriptionFa?: string;
  displayOrder?: number;
  xpScore?: number;
  mandatoryForPassingLesson?: boolean;
  isDesignedForNonFarsiUsers?: boolean;
  questions: CreateUpdateLessonExerciseQuestionDto[];
  subscriptionPlanIds?: number[];
  categoryIds?: number[];
  placement?: LessonExercisePlacement;
}

export interface CreateUpdateLessonExerciseQuestionDto {
  id?: number;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  explanation?: string;
  explanationFa?: string;
  points?: number;
  displayOrder?: number;
  questionType?: QuestionType;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  correctOptionId?: number;
  puzzleAlignment?: string;
  gridRows?: number;
  gridCols?: number;
  gridLetters?: string;
  minAnswerLength?: number;
  maxAnswerLength?: number;
  allowHorizontal?: boolean;
  allowVertical?: boolean;
  allowDiagonal?: boolean;
  gridFillMode?: string;
  questionOptions?: CreateUpdateLessonExerciseQuestionOptionDto[];
}

export interface CreateUpdateLessonExerciseQuestionOptionDto {
  id?: number;
  displayOrder?: number;
  correctOrder?: number;
  startRow?: number;
  startCol?: number;
  wordDirection?: string;
  entryWord?: string;
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  isCorrect?: boolean;
}

export interface RichContentBlockBackfillResult {
  ownersMigrated: number;
  blocksInserted: number;
  skippedAlreadyMigrated: number;
  skippedEmpty: number;
  migratedByOwnerType: Record<string, number>;
}

// ============================================================================
// CONTENT SERVICE
// ============================================================================

/** Admin editor GETs must bypass apiService cache so reopening shows latest questions. */
const FRESH_ADMIN_READ = { cache: false as const };

/** Learner content GETs must bypass cache so admin edits appear after a normal refresh. */
const FRESH_LEARNER_READ = { cache: false as const };

/** Large lesson/quiz/exercise payloads can exceed the default 30s upload window. */
const ADMIN_CONTENT_WRITE = { timeout: 120_000, retries: 0 } as const;

function invalidateContentAdminCache(): void {
  apiService.clearCache();
}

function isAdminNotFoundError(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === 'object' &&
      'statusCode' in error &&
      (error as { statusCode?: number }).statusCode === 404
  );
}

async function verifyAdminEntityGone(load: () => Promise<unknown>): Promise<boolean> {
  try {
    await load();
    return false;
  } catch (error) {
    return isAdminNotFoundError(error);
  }
}

class ContentService {
  // Admin Course Operations
  async getAllCourses(forceRefresh: boolean = false): Promise<CourseDto[]> {
    // Force bypass cache if forceRefresh is true
    return apiService.get<CourseDto[]>('/api/Content/admin/courses', {
      cache: !forceRefresh
    });
  }

  async getCoursesSummary(forceRefresh: boolean = false): Promise<CourseDto[]> {
    // Returns courses without levels for lazy loading
    return apiService.get<CourseDto[]>(`/api/Content/admin/courses/summary${forceRefresh ? '?forceRefresh=true' : ''}`);
  }

  async getLevelsSummaryByCourseId(courseId: number): Promise<LevelDto[]> {
    // Returns levels without lessons for lazy loading
    return apiService.get<LevelDto[]>(`/api/Content/admin/courses/${courseId}/levels/summary`);
  }

  async getCourseById(courseId: number): Promise<CourseDto> {
    return apiService.get<CourseDto>(`/api/Content/admin/courses/${courseId}`);
  }

  async createCourse(createDto: CreateUpdateCourseDto): Promise<CourseDto> {
    return apiService.post<CourseDto>('/api/Content/admin/courses', createDto, ADMIN_CONTENT_WRITE);
  }

  async updateCourse(courseId: number, updateDto: CreateUpdateCourseDto): Promise<CourseDto> {
    return apiService.put<CourseDto>(`/api/Content/admin/courses/${courseId}`, updateDto, ADMIN_CONTENT_WRITE);
  }

  async deleteCourse(courseId: number): Promise<void> {
    await apiService.delete(`/api/Content/admin/courses/${courseId}`);
    // Clear cache for courses list to ensure fresh data
    apiService.clearCache();
  }

  async reorderCourses(reorderDto: ReorderItemsDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>('/api/Content/admin/courses/reorder', reorderDto);
  }

  async reorderCourseQuizzes(courseId: number, reorderDto: ReorderItemsDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(
      `/api/Content/admin/courses/${courseId}/quizzes/reorder`,
      reorderDto
    );
  }

  async reorderCourseExercises(courseId: number, reorderDto: ReorderItemsDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(
      `/api/Content/admin/courses/${courseId}/exercises/reorder`,
      reorderDto
    );
  }

  async publishCourse(courseId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/courses/${courseId}/publish`);
  }

  async unpublishCourse(courseId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/courses/${courseId}/unpublish`);
  }

  async validateCourseForPublish(courseId: number): Promise<{ errors: string[]; isValid: boolean }> {
    return apiService.get<{ errors: string[]; isValid: boolean }>(`/api/Content/admin/courses/${courseId}/validate`);
  }

  async validateLevelForPublish(levelId: number): Promise<{ errors: string[]; isValid: boolean }> {
    return apiService.get<{ errors: string[]; isValid: boolean }>(`/api/Content/admin/levels/${levelId}/validate`);
  }

  async validateLessonForPublish(lessonId: number): Promise<{ errors: string[]; isValid: boolean }> {
    return apiService.get<{ errors: string[]; isValid: boolean }>(`/api/Content/admin/lessons/${lessonId}/validate`);
  }

  // Admin Level Operations
  async getLevelsByCourseId(courseId: number): Promise<LevelDto[]> {
    return apiService.get<LevelDto[]>(`/api/Content/admin/courses/${courseId}/levels`);
  }

  async getLevelById(levelId: number): Promise<LevelDto> {
    return apiService.get<LevelDto>(`/api/Content/admin/levels/${levelId}`);
  }

  async createLevel(courseId: number, createDto: CreateUpdateLevelDto): Promise<LevelDto> {
    return apiService.post<LevelDto>(`/api/Content/admin/courses/${courseId}/levels`, createDto, ADMIN_CONTENT_WRITE);
  }

  async updateLevel(levelId: number, updateDto: CreateUpdateLevelDto): Promise<LevelDto> {
    return apiService.put<LevelDto>(`/api/Content/admin/levels/${levelId}`, updateDto, ADMIN_CONTENT_WRITE);
  }

  async deleteLevel(levelId: number): Promise<void> {
    await apiService.delete(`/api/Content/admin/levels/${levelId}`);
    // Clear cache to ensure fresh data
    apiService.clearCache();
  }

  async reorderLevels(courseId: number, reorderDto: ReorderItemsDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/courses/${courseId}/levels/reorder`, reorderDto);
  }

  async publishLevel(levelId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/levels/${levelId}/publish`);
  }

  async unpublishLevel(levelId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/levels/${levelId}/unpublish`);
  }

  // Admin Lesson Operations
  async getLessonsByLevelId(levelId: number): Promise<LessonDto[]> {
    return apiService.get<LessonDto[]>(`/api/Content/admin/levels/${levelId}/lessons`);
  }

  /** Lessons without content blocks/quiz details - for fast hierarchy tree. */
  async getLessonsSummaryByLevelId(levelId: number): Promise<LessonDto[]> {
    return apiService.get<LessonDto[]>(`/api/Content/admin/levels/${levelId}/lessons/summary`);
  }

  async getLessonById(lessonId: number): Promise<LessonDto> {
    return apiService.get<LessonDto>(`/api/Content/admin/lessons/${lessonId}`, FRESH_ADMIN_READ);
  }

  async createLesson(levelId: number, createDto: CreateUpdateLessonDto): Promise<LessonDto> {
    return apiService.post<LessonDto>(`/api/Content/admin/levels/${levelId}/lessons`, createDto, ADMIN_CONTENT_WRITE);
  }

  async copyLesson(lessonId: number, targetLevelId: number): Promise<LessonDto> {
    const result = await apiService.post<LessonDto>(
      `/api/Content/admin/lessons/${lessonId}/copy`,
      { targetLevelId },
      ADMIN_CONTENT_WRITE
    );
    invalidateContentAdminCache();
    return result;
  }

  async copyCourseQuiz(quizId: number, targetCourseId: number): Promise<{ id: number; title: string }> {
    return this.copyHierarchyItem(`/api/Content/admin/course-quizzes/${quizId}/copy`, targetCourseId);
  }

  async copyLevelQuiz(quizId: number, targetLevelId: number): Promise<{ id: number; title: string }> {
    return this.copyHierarchyItem(`/api/Content/admin/level-quizzes/${quizId}/copy`, targetLevelId);
  }

  async copyLessonQuiz(quizId: number, targetLessonId: number): Promise<{ id: number; title: string }> {
    return this.copyHierarchyItem(`/api/Content/admin/lesson-quizzes/${quizId}/copy`, targetLessonId);
  }

  async copyCourseExercise(exerciseId: number, targetCourseId: number): Promise<{ id: number; title: string }> {
    return this.copyHierarchyItem(`/api/Content/admin/course-exercises/${exerciseId}/copy`, targetCourseId);
  }

  async copyLevelExercise(exerciseId: number, targetLevelId: number): Promise<{ id: number; title: string }> {
    return this.copyHierarchyItem(`/api/Content/admin/level-exercises/${exerciseId}/copy`, targetLevelId);
  }

  async copyLessonExercise(exerciseId: number, targetLessonId: number): Promise<{ id: number; title: string }> {
    return this.copyHierarchyItem(`/api/Content/admin/lesson-exercises/${exerciseId}/copy`, targetLessonId);
  }

  async copyActivity(
    sourceKind: string,
    sourceId: number,
    targetKind: 'course' | 'level' | 'lesson',
    targetId: number
  ): Promise<{ id: number; title: string }> {
    const result = await apiService.post<{ id: number; title: string }>(
      '/api/Content/admin/activities/copy',
      { sourceKind, sourceId, targetKind, targetId },
      ADMIN_CONTENT_WRITE
    );
    invalidateContentAdminCache();
    return result;
  }

  private async copyHierarchyItem(url: string, targetId: number): Promise<{ id: number; title: string }> {
    const result = await apiService.post<{ id: number; title: string }>(url, { targetId }, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async updateLesson(lessonId: number, updateDto: CreateUpdateLessonDto): Promise<LessonDto> {
    const result = await apiService.put<LessonDto>(`/api/Content/admin/lessons/${lessonId}`, updateDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  /** Update only lesson quizzes. Does not touch lesson fields, blocks, categories, or subscription plans. */
  async updateLessonQuizzesOnly(lessonId: number, quizzes: CreateUpdateLessonQuizDto[]): Promise<LessonDto> {
    const result = await apiService.put<LessonDto>(`/api/Content/admin/lessons/${lessonId}/quizzes`, quizzes, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  /** Remove all lesson quizzes (hierarchy delete). */
  async clearLessonQuizzes(lessonId: number): Promise<void> {
    await apiService.put<LessonDto>(`/api/Content/admin/lessons/${lessonId}/quizzes`, [], ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
  }

  async verifyLessonQuizDeleted(lessonId: number): Promise<boolean> {
    try {
      const lesson = await this.getLessonById(lessonId);
      const quizzes =
        (lesson as { quizzes?: LessonQuizDto[] }).quizzes ??
        (lesson.lessonQuiz ? [lesson.lessonQuiz] : []);
      return quizzes.length === 0;
    } catch (error) {
      return isAdminNotFoundError(error);
    }
  }

  async deleteLesson(lessonId: number): Promise<void> {
    await apiService.delete(`/api/Content/admin/lessons/${lessonId}`, ADMIN_CONTENT_WRITE);
    // Clear cache to ensure fresh data
    apiService.clearCache();
  }

  async getRemoveImpact(kind: ContentTrashKind, id: number): Promise<ContentRemoveImpactDto> {
    return apiService.get<ContentRemoveImpactDto>(
      `/api/Content/admin/trash/${kind}/${id}/impact`,
      ADMIN_CONTENT_WRITE
    );
  }

  async removeContent(kind: ContentTrashKind, id: number): Promise<ContentRemoveResultDto> {
    const result = await apiService.post<ContentRemoveResultDto>(
      `/api/Content/admin/trash/${kind}/${id}/remove`,
      {},
      ADMIN_CONTENT_WRITE
    );
    apiService.clearCache();
    invalidateContentAdminCache();
    return result;
  }

  async restoreContent(kind: ContentTrashKind, id: number): Promise<void> {
    await apiService.post(`/api/Content/admin/trash/${kind}/${id}/restore`, {}, ADMIN_CONTENT_WRITE);
    apiService.clearCache();
    invalidateContentAdminCache();
  }

  async listTrashedContent(): Promise<ContentTrashItemDto[]> {
    return apiService.get<ContentTrashItemDto[]>('/api/Content/admin/trash', ADMIN_CONTENT_WRITE);
  }

  // Statistics
  async getContentManagementStats(): Promise<any> {
    return apiService.get('/api/Content/admin/stats');
  }

  async backfillRichContentBlocks(): Promise<RichContentBlockBackfillResult> {
    const result = await apiService.post<RichContentBlockBackfillResult>(
      '/api/Content/admin/rich-content-blocks/backfill',
      {},
      { timeout: 300_000, retries: 0 }
    );
    invalidateContentAdminCache();
    return result;
  }

  async reorderLessons(levelId: number, reorderDto: ReorderItemsDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/levels/${levelId}/lessons/reorder`, reorderDto);
  }

  async reorderLevelQuizzes(levelId: number, reorderDto: ReorderItemsDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(
      `/api/Content/admin/levels/${levelId}/quizzes/reorder`,
      reorderDto
    );
  }

  async reorderLevelExercises(levelId: number, reorderDto: ReorderItemsDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(
      `/api/Content/admin/levels/${levelId}/exercises/reorder`,
      reorderDto
    );
  }

  async reorderLessonExercises(lessonId: number, reorderDto: ReorderItemsDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(
      `/api/Content/admin/lessons/${lessonId}/exercises/reorder`,
      reorderDto
    );
  }

  async reorderLessonQuizzes(lessonId: number, reorderDto: ReorderItemsDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(
      `/api/Content/admin/lessons/${lessonId}/quizzes/reorder`,
      reorderDto
    );
  }

  async publishLesson(lessonId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/lessons/${lessonId}/publish`);
  }

  async unpublishLesson(lessonId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/lessons/${lessonId}/unpublish`);
  }

  // Admin Course Quiz Operations
  async createCourseQuiz(courseId: number, createDto: CreateUpdateCourseQuizDto): Promise<CourseQuizDto> {
    const result = await apiService.post<CourseQuizDto>(`/api/Content/admin/courses/${courseId}/quizzes`, createDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async updateCourseQuiz(quizId: number, updateDto: CreateUpdateCourseQuizDto): Promise<CourseQuizDto> {
    const result = await apiService.put<CourseQuizDto>(`/api/Content/admin/courses/quizzes/${quizId}`, updateDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async deleteCourseQuiz(quizId: number): Promise<void> {
    await apiService.delete(`/api/Content/admin/courses/quizzes/${quizId}`, ADMIN_CONTENT_WRITE);
    apiService.clearCache();
  }

  async verifyCourseQuizDeleted(quizId: number): Promise<boolean> {
    return verifyAdminEntityGone(() => this.getCourseQuizById(quizId));
  }

  async getCourseQuizById(quizId: number): Promise<CourseQuizDto> {
    return apiService.get<CourseQuizDto>(`/api/Content/admin/courses/quizzes/${quizId}`, FRESH_ADMIN_READ);
  }

  async getCourseQuizzesByCourseId(courseId: number): Promise<CourseQuizDto[]> {
    return apiService.get<CourseQuizDto[]>(`/api/Content/admin/courses/${courseId}/quizzes`, FRESH_ADMIN_READ);
  }

  async publishCourseQuiz(quizId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/courses/quizzes/${quizId}/publish`);
  }

  async unpublishCourseQuiz(quizId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/courses/quizzes/${quizId}/unpublish`);
  }

  // Admin Level Quiz Operations
  async createLevelQuiz(levelId: number, createDto: CreateUpdateLevelQuizDto): Promise<LevelQuizDto> {
    const result = await apiService.post<LevelQuizDto>(`/api/Content/admin/levels/${levelId}/quizzes`, createDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async updateLevelQuiz(quizId: number, updateDto: CreateUpdateLevelQuizDto): Promise<LevelQuizDto> {
    const result = await apiService.put<LevelQuizDto>(`/api/Content/admin/levels/quizzes/${quizId}`, updateDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async deleteLevelQuiz(quizId: number): Promise<void> {
    await apiService.delete(`/api/Content/admin/levels/quizzes/${quizId}`, ADMIN_CONTENT_WRITE);
    apiService.clearCache();
  }

  async verifyLevelQuizDeleted(quizId: number): Promise<boolean> {
    return verifyAdminEntityGone(() => this.getLevelQuizById(quizId));
  }

  async getLevelQuizById(quizId: number): Promise<LevelQuizDto> {
    return apiService.get<LevelQuizDto>(`/api/Content/admin/levels/quizzes/${quizId}`, FRESH_ADMIN_READ);
  }

  async getLevelQuizByIdForLearner(quizId: number): Promise<LevelQuizDto> {
    return apiService.get<LevelQuizDto>(`/api/Content/levels/quizzes/${quizId}`, FRESH_LEARNER_READ);
  }

  async getLessonQuizByIdForLearner(quizId: number): Promise<LessonQuizDto> {
    return apiService.get<LessonQuizDto>(`/api/Content/lessons/quizzes/${quizId}`, FRESH_LEARNER_READ);
  }

  async getCourseQuizByIdForLearner(quizId: number): Promise<CourseQuizDto> {
    return apiService.get<CourseQuizDto>(`/api/Content/courses/quizzes/${quizId}`, FRESH_LEARNER_READ);
  }

  // Exercise (learner) – get exercise and check answer (no time limit / passing score)
  async getCourseExerciseForLearner(exerciseId: number): Promise<CourseExerciseDto> {
    return apiService.get<CourseExerciseDto>(`/api/Content/courses/exercises/${exerciseId}`, FRESH_LEARNER_READ);
  }

  async getLevelExerciseForLearner(exerciseId: number): Promise<LevelExerciseDto> {
    return apiService.get<LevelExerciseDto>(`/api/Content/levels/exercises/${exerciseId}`, FRESH_LEARNER_READ);
  }

  async getLessonExerciseForLearner(exerciseId: number): Promise<LessonExerciseDto> {
    return apiService.get<LessonExerciseDto>(`/api/Content/lessons/exercises/${exerciseId}`, FRESH_LEARNER_READ);
  }

  async checkCourseExerciseQuestion(exerciseId: number, questionId: number, answer: string): Promise<ExerciseQuestionCheckResultDto> {
    return apiService.post<ExerciseQuestionCheckResultDto>(`/api/Content/courses/exercises/${exerciseId}/questions/${questionId}/check`, { answer });
  }

  async checkLevelExerciseQuestion(exerciseId: number, questionId: number, answer: string): Promise<ExerciseQuestionCheckResultDto> {
    return apiService.post<ExerciseQuestionCheckResultDto>(`/api/Content/levels/exercises/${exerciseId}/questions/${questionId}/check`, { answer });
  }

  async checkLessonExerciseQuestion(exerciseId: number, questionId: number, answer: string): Promise<ExerciseQuestionCheckResultDto> {
    return apiService.post<ExerciseQuestionCheckResultDto>(`/api/Content/lessons/exercises/${exerciseId}/questions/${questionId}/check`, { answer });
  }

  // Exercise Admin API
  async getCourseExercisesByCourseId(courseId: number): Promise<CourseExerciseDto[]> {
    return apiService.get<CourseExerciseDto[]>(`/api/Content/admin/courses/${courseId}/exercises`, FRESH_ADMIN_READ);
  }

  async getCourseExerciseById(exerciseId: number): Promise<CourseExerciseDto> {
    return apiService.get<CourseExerciseDto>(`/api/Content/admin/courses/exercises/${exerciseId}`, FRESH_ADMIN_READ);
  }

  async createCourseExercise(courseId: number, createDto: CreateUpdateCourseExerciseDto): Promise<CourseExerciseDto> {
    const result = await apiService.post<CourseExerciseDto>(`/api/Content/admin/courses/${courseId}/exercises`, createDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async updateCourseExercise(exerciseId: number, updateDto: CreateUpdateCourseExerciseDto): Promise<CourseExerciseDto> {
    const result = await apiService.put<CourseExerciseDto>(`/api/Content/admin/courses/exercises/${exerciseId}`, updateDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async deleteCourseExercise(exerciseId: number): Promise<void> {
    await apiService.delete(`/api/Content/admin/courses/exercises/${exerciseId}`, ADMIN_CONTENT_WRITE);
    apiService.clearCache();
  }

  async verifyCourseExerciseDeleted(exerciseId: number): Promise<boolean> {
    return verifyAdminEntityGone(() => this.getCourseExerciseById(exerciseId));
  }

  async publishCourseExercise(exerciseId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/courses/exercises/${exerciseId}/publish`);
  }

  async unpublishCourseExercise(exerciseId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/courses/exercises/${exerciseId}/unpublish`);
  }

  async getLevelExercisesByLevelId(levelId: number): Promise<LevelExerciseDto[]> {
    return apiService.get<LevelExerciseDto[]>(`/api/Content/admin/levels/${levelId}/exercises`, FRESH_ADMIN_READ);
  }

  async getLevelExerciseById(exerciseId: number): Promise<LevelExerciseDto> {
    return apiService.get<LevelExerciseDto>(`/api/Content/admin/levels/exercises/${exerciseId}`, FRESH_ADMIN_READ);
  }

  async createLevelExercise(levelId: number, createDto: CreateUpdateLevelExerciseDto): Promise<LevelExerciseDto> {
    const result = await apiService.post<LevelExerciseDto>(`/api/Content/admin/levels/${levelId}/exercises`, createDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async updateLevelExercise(exerciseId: number, updateDto: CreateUpdateLevelExerciseDto): Promise<LevelExerciseDto> {
    const result = await apiService.put<LevelExerciseDto>(`/api/Content/admin/levels/exercises/${exerciseId}`, updateDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async deleteLevelExercise(exerciseId: number): Promise<void> {
    await apiService.delete(`/api/Content/admin/levels/exercises/${exerciseId}`, ADMIN_CONTENT_WRITE);
    apiService.clearCache();
  }

  async verifyLevelExerciseDeleted(exerciseId: number): Promise<boolean> {
    return verifyAdminEntityGone(() => this.getLevelExerciseById(exerciseId));
  }

  async publishLevelExercise(exerciseId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/levels/exercises/${exerciseId}/publish`);
  }

  async unpublishLevelExercise(exerciseId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/levels/exercises/${exerciseId}/unpublish`);
  }

  async getLessonExercisesByLessonId(lessonId: number): Promise<LessonExerciseDto[]> {
    return apiService.get<LessonExerciseDto[]>(`/api/Content/admin/lessons/${lessonId}/exercises`, FRESH_ADMIN_READ);
  }

  async getLessonExerciseById(exerciseId: number): Promise<LessonExerciseDto> {
    return apiService.get<LessonExerciseDto>(`/api/Content/admin/lessons/exercises/${exerciseId}`, FRESH_ADMIN_READ);
  }

  async createLessonExercise(lessonId: number, createDto: CreateUpdateLessonExerciseDto): Promise<LessonExerciseDto> {
    const result = await apiService.post<LessonExerciseDto>(`/api/Content/admin/lessons/${lessonId}/exercises`, createDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async updateLessonExercise(exerciseId: number, updateDto: CreateUpdateLessonExerciseDto): Promise<LessonExerciseDto> {
    const result = await apiService.put<LessonExerciseDto>(`/api/Content/admin/lessons/exercises/${exerciseId}`, updateDto, ADMIN_CONTENT_WRITE);
    invalidateContentAdminCache();
    return result;
  }

  async deleteLessonExercise(exerciseId: number): Promise<void> {
    await apiService.delete(`/api/Content/admin/lessons/exercises/${exerciseId}`, ADMIN_CONTENT_WRITE);
    apiService.clearCache();
  }

  async verifyLessonExerciseDeleted(exerciseId: number): Promise<boolean> {
    return verifyAdminEntityGone(() => this.getLessonExerciseById(exerciseId));
  }

  async publishLessonExercise(exerciseId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/lessons/exercises/${exerciseId}/publish`);
  }

  async unpublishLessonExercise(exerciseId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/lessons/exercises/${exerciseId}/unpublish`);
  }

  async getLevelQuizzesByLevelId(levelId: number): Promise<LevelQuizDto[]> {
    return apiService.get<LevelQuizDto[]>(`/api/Content/admin/levels/${levelId}/quizzes`, FRESH_ADMIN_READ);
  }

  async publishLevelQuiz(quizId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/levels/quizzes/${quizId}/publish`);
  }

  async unpublishLevelQuiz(quizId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/levels/quizzes/${quizId}/unpublish`);
  }

  async publishLessonQuiz(quizId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/lessons/quizzes/${quizId}/publish`);
  }

  async unpublishLessonQuiz(quizId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/admin/lessons/quizzes/${quizId}/unpublish`);
  }

  // Subscriber Operations
  async getAvailableCourses(): Promise<CourseCatalogDto[]> {
    return apiService.get<CourseCatalogDto[]>('/api/Content/courses', { cache: false });
  }

  async getPublicQuickLaunchCards(): Promise<PublicQuickLaunchCardDto[]> {
    return apiService.get<PublicQuickLaunchCardDto[]>('/api/Content/quick-launch-cards', { cache: false });
  }

  async searchLearnerContent(request: LearnerSearchRequest): Promise<LearnerSearchResult[]> {
    const body = {
      query: request.query,
      searchCourseTitles: request.searchCourseTitles ?? true,
      searchLevelTitles: request.searchLevelTitles ?? true,
      searchLessonTitles: request.searchLessonTitles ?? true,
      searchBlockTitles: request.searchBlockTitles ?? true,
      searchTextBlocks: request.searchTextBlocks ?? true,
      searchCourseCatalog: request.searchCourseCatalog ?? true,
      searchLevelCatalog: request.searchLevelCatalog ?? true,
      searchLessonQuizzes: request.searchLessonQuizzes ?? true,
      searchLessonExercises: request.searchLessonExercises ?? true,
      searchLibraryBookTitles: request.searchLibraryBookTitles ?? true,
      searchLibraryBookText: request.searchLibraryBookText ?? true,
      searchVocabulary: request.searchVocabulary ?? true,
      searchLeitner: request.searchLeitner ?? true,
      limitToLearningContext: request.limitToLearningContext ?? false,
      scopeCourseId: request.scopeCourseId,
      scopeLevelId: request.scopeLevelId,
      scopeLessonId: request.scopeLessonId,
      scopeLimitToLibrary: request.scopeLimitToLibrary ?? false,
      scopeLibraryBookId: request.scopeLibraryBookId
    };
    const raw = await apiService.post<unknown[]>('/api/Content/learner-search', body, { cache: false });
    if (!Array.isArray(raw)) return [];
    return raw.map((row) => {
      const r = row as Record<string, unknown>;
      const mk = String(r.matchKind ?? '') as LearnerSearchMatchKind;
      const item: LearnerSearchResult = {
        matchKind: mk,
        subKind: String(r.subKind ?? ''),
        label: String(r.label ?? ''),
        courseId: Number(r.courseId),
        courseTitle: String(r.courseTitle ?? '')
      };
      if (r.snippet != null) item.snippet = String(r.snippet);
      if (r.rankOrder != null) item.rankOrder = Number(r.rankOrder);
      if (r.levelId != null) item.levelId = Number(r.levelId);
      if (r.levelTitle != null) item.levelTitle = String(r.levelTitle);
      if (r.lessonId != null) item.lessonId = Number(r.lessonId);
      if (r.lessonTitle != null) item.lessonTitle = String(r.lessonTitle);
      if (r.contentBlockId != null) item.contentBlockId = Number(r.contentBlockId);
      if (r.blockTitle != null) item.blockTitle = String(r.blockTitle);
      if (r.lessonQuizId != null) item.lessonQuizId = Number(r.lessonQuizId);
      if (r.lessonExerciseId != null) item.lessonExerciseId = Number(r.lessonExerciseId);
      if (r.libraryBookId != null) item.libraryBookId = Number(r.libraryBookId);
      if (r.LibraryBookId != null) item.libraryBookId = Number(r.LibraryBookId);
      if (r.libraryBookTitle != null) item.libraryBookTitle = String(r.libraryBookTitle);
      if (r.LibraryBookTitle != null) item.libraryBookTitle = String(r.LibraryBookTitle);
      const rawLeitnerId = r.leitnerCardId ?? r.LeitnerCardId;
      if (rawLeitnerId != null && Number(rawLeitnerId) > 0) {
        item.leitnerCardId = Number(rawLeitnerId);
      }
      const rawBlockId = r.libraryBookBlockId ?? r.LibraryBookBlockId;
      if (rawBlockId != null && Number(rawBlockId) > 0) {
        item.libraryBookBlockId = Number(rawBlockId);
      }
      if (r.isLocked === true) item.isLocked = true;
      if (typeof r.lockedAt === 'string' && r.lockedAt.length > 0) {
        const at = r.lockedAt as 'Course' | 'Level' | 'Lesson' | 'Exercise' | 'Book';
        item.lockedAt = at;
      }
      if (Array.isArray(r.requiredPlans)) {
        item.requiredPlans = r.requiredPlans
          .map((p) => p as Record<string, unknown>)
          .map((p) => {
            const plan: LearnerSearchRequiredPlan = {
              id: Number(p.id),
              name: String(p.name ?? '')
            };
            if (p.description != null && String(p.description).trim().length > 0) {
              plan.description = String(p.description);
            }
            return plan;
          });
      }
      return item;
    });
  }

  async getCourseDetail(courseId: number): Promise<CourseDetailDto> {
    return apiService.get<CourseDetailDto>(`/api/Content/courses/${courseId}`, { cache: false });
  }

  async getLevelDetail(levelId: number): Promise<LevelDetailDto> {
    return apiService.get<LevelDetailDto>(`/api/Content/levels/${levelId}`, FRESH_LEARNER_READ);
  }

  async getLessonDetail(lessonId: number, options?: { signal?: AbortSignal; timeout?: number }): Promise<LessonDetailDto> {
    const config: { signal?: AbortSignal; timeout?: number; cache?: boolean } = { cache: false };
    if (options?.signal != null) config.signal = options.signal;
    if (options?.timeout != null) config.timeout = options.timeout;
    return apiService.get<LessonDetailDto>(`/api/Content/lessons/${lessonId}`, config);
  }

  async getStickyAudioMap(): Promise<StickyAudioMapEntryDto[]> {
    return apiService.get<StickyAudioMapEntryDto[]>('/api/Content/sticky-audio', { cache: false });
  }

  async getCourseProgress(courseId: number): Promise<UserCourseProgressDto | null> {
    return apiService.get<UserCourseProgressDto | null>(`/api/Content/courses/${courseId}/progress`);
  }

  async updateLessonProgress(lessonId: number, progressDto: UpdateLessonProgressDto): Promise<{ success: boolean; xpAwarded?: number }> {
    return apiService.post<{ success: boolean; xpAwarded?: number }>(`/api/Content/lessons/${lessonId}/progress`, progressDto);
  }

  async completeLessonExercise(
    exerciseId: number,
    request: CompleteLessonExerciseRequestDto
  ): Promise<CompleteLessonExerciseResultDto> {
    return apiService.post<CompleteLessonExerciseResultDto>(
      `/api/Content/lessons/exercises/${exerciseId}/complete`,
      request
    );
  }

  async submitLessonQuiz(lessonId: number, submission: LessonQuizSubmissionDto): Promise<LessonQuizResultDto> {
    return apiService.post<LessonQuizResultDto>(`/api/Content/lessons/${lessonId}/quiz/submit`, submission);
  }

  // Quiz Flow Operations
  async getLevelQuizzesForQuizFlow(): Promise<LevelQuizWithContextDto[]> {
    return apiService.get<LevelQuizWithContextDto[]>('/api/Content/quiz/level-quizzes');
  }

  async getQuizFlowItems(): Promise<QuizFlowItemDto[]> {
    const raw = await apiService.get<unknown[]>('/api/Content/quiz/flow-items', { cache: false });
    return Array.isArray(raw) ? raw.map(normalizeQuizFlowItem) : [];
  }

  async getExerciseFlowItems(): Promise<ExerciseFlowItemDto[]> {
    const raw = await apiService.get<unknown[]>('/api/Content/exercises/flow-items', { cache: false });
    return Array.isArray(raw) ? raw.map(normalizeExerciseFlowItem) : [];
  }

  async markExerciseReviewed(scope: string, exerciseId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>('/api/Content/exercises/mark-reviewed', {
      scope,
      exerciseId
    });
  }

  async markExerciseUnreviewed(scope: string, exerciseId: number): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>('/api/Content/exercises/unmark-reviewed', {
      scope,
      exerciseId
    });
  }

  async submitLevelQuiz(submission: LevelQuizSubmissionDto): Promise<LevelQuizResultDto> {
    return apiService.post<LevelQuizResultDto>('/api/Content/quiz/level-quiz/submit', submission);
  }

  // Time Tracking Operations
  async submitTimeTracking(contentBlockId: number, timeDto: SubmitTimeTrackingDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/blocks/${contentBlockId}/track-time`, timeDto);
  }

  async recordExerciseTimeSession(body: RecordExerciseTimeSessionDto): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>('/api/Content/exercises/time-session', body);
  }

  async getBlockTimeTracking(lessonId: number): Promise<BlockTimeTrackingDto[]> {
    return apiService.get<BlockTimeTrackingDto[]>(`/api/Content/lessons/${lessonId}/time-tracking`);
  }

  // Enhanced Progress with Time Tracking
  async getCourseProgressWithTime(courseId: number): Promise<CourseProgressWithTimeDto | null> {
    return apiService.get<CourseProgressWithTimeDto | null>(`/api/Content/courses/${courseId}/progress-detailed`, { cache: false });
  }

  async getCoursesProgressWithTime(): Promise<CourseProgressWithTimeDto[]> {
    const data = await apiService.get<CourseProgressWithTimeDto[]>('/api/Content/courses/progress-detailed', { cache: false });
    return Array.isArray(data) ? data : [];
  }

  async getLevelProgressWithTime(levelId: number): Promise<LevelProgressWithTimeDto | null> {
    return apiService.get<LevelProgressWithTimeDto | null>(`/api/Content/levels/${levelId}/progress-detailed`);
  }

  // Enhanced Quiz Submission
  async submitLessonQuizWithGuidance(lessonId: number, submission: LessonQuizSubmissionDto): Promise<EnhancedQuizResultDto> {
    return apiService.post<EnhancedQuizResultDto>(`/api/Content/lessons/${lessonId}/quiz/submit-enhanced`, submission);
  }

  async submitLevelQuizWithGuidance(submission: LevelQuizSubmissionDto): Promise<EnhancedQuizResultDto> {
    return apiService.post<EnhancedQuizResultDto>('/api/Content/quiz/level-quiz/submit-enhanced', submission);
  }

  async submitCourseQuiz(courseId: number, submission: CourseQuizSubmissionDto): Promise<EnhancedQuizResultDto> {
    return apiService.post<EnhancedQuizResultDto>(`/api/Content/courses/${courseId}/quiz/submit`, submission);
  }

  async submitCourseQuizWithGuidance(submission: CourseQuizSubmissionDto): Promise<EnhancedQuizResultDto> {
    return apiService.post<EnhancedQuizResultDto>('/api/Content/quiz/course-quiz/submit-enhanced', submission);
  }

  // Tag Browsing
  async getBlocksByTag(tagId: number): Promise<TaggedContentBlockDto[]> {
    return apiService.get<TaggedContentBlockDto[]>(`/api/Content/tags/${tagId}/blocks`, FRESH_LEARNER_READ);
  }

  async getLessonsByTag(tagId: number): Promise<TaggedContentLessonDto[]> {
    return apiService.get<TaggedContentLessonDto[]>(`/api/Content/tags/${tagId}/lessons`, FRESH_LEARNER_READ);
  }

  async getTagProgress(tagId: number): Promise<TagProgressDto> {
    return apiService.get<TagProgressDto>(`/api/Content/tags/${tagId}/progress`, { cache: false });
  }

  async getTagsProgress(): Promise<TagProgressDto[]> {
    return apiService.get<TagProgressDto[]>('/api/Content/tags/progress');
  }

  async recordTagBlockLessonView(tagId: number, blockId: number): Promise<{ viewCount: number }> {
    return apiService.post<{ viewCount: number }>(`/api/Content/tags/${tagId}/blocks/${blockId}/view`, {});
  }

  async setTagBlockProgress(tagId: number, blockId: number, completed: boolean): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/tags/${tagId}/blocks/${blockId}/progress`, { completed });
  }

  async setTagLessonProgress(tagId: number, lessonId: number, completed: boolean): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(`/api/Content/tags/${tagId}/lessons/${lessonId}/progress`, { completed });
  }

  // Placement Quiz Session Operations
  async startPlacementQuizSession(): Promise<PlacementQuizSessionDto> {
    return apiService.post<PlacementQuizSessionDto>('/api/Content/placement/start', {});
  }

  async getPlacementQuizSession(sessionId: string): Promise<PlacementQuizSessionDto | null> {
    return apiService.get<PlacementQuizSessionDto | null>(`/api/Content/placement/session/${sessionId}`);
  }

  async getActivePlacementQuizSession(): Promise<PlacementQuizSessionDto | null> {
    return apiService.get<PlacementQuizSessionDto | null>('/api/Content/placement/active');
  }

  async submitPlacementLevelQuiz(sessionId: string, submission: LevelQuizSubmissionDto): Promise<PlacementQuizResultDto> {
    return apiService.post<PlacementQuizResultDto>(`/api/Content/placement/session/${sessionId}/submit`, submission);
  }

  async completePlacementQuizSession(sessionId: string): Promise<PlacementQuizRecommendationDto | null> {
    return apiService.post<PlacementQuizRecommendationDto | null>(`/api/Content/placement/session/${sessionId}/complete`, {});
  }
}

export const contentService = new ContentService();

